import type { Language } from '@worldathletics/types';
import { isLostInternalReference } from '@worldathletics/utilities';

import type { EditLinkSourceRef } from '@/components/modals/edit-link';

import { resolveChipRefRowForOccurrence } from './resolve-chip-ref-row';
import { resolvePickerSource } from './reference-picker-source';
import { collectClaimedRowIds, isRowScopedReferenceId } from './reference-token-id';
import type { ReferenceRowLookupResult } from './reference-row-index';
import { resolveSerializedWalkRoot } from './resolve-serialized-walk-root';
import { computeSerializedOffsets } from './serialized-offsets';
import {
  findTokenPositionAtOccurrence,
  tokenNeedle,
} from './text-token-ops';

export type EditLinkRefRow = {
  id: string;
  sourceId: string;
  refId: string | null;
  targetBookId?: string | null;
  targetNodeId?: string | null;
  customName?: string | null;
  detectedPhrase?: string | null;
  kind?: string;
  status?: string;
  resolved?: boolean;
};

/**
 * Resolve a chip's `data-ref-id` to the row THAT occurrence means: a
 * row-scoped id already names exactly one row; a legacy target-id token
 * shared by several rows resolves by the chip's occurrence index among DOM
 * chips sharing the same raw id, excluding any row a sibling occurrence in
 * this block has already claimed via its own row-scoped token (otherwise a
 * block half-migrated by an earlier edit would hand the remaining legacy
 * occurrence back onto the row a sibling already owns).
 */
function resolveOccurrenceAwareRow<T extends EditLinkRefRow>(
  refs: T[] | undefined,
  sourceBlockId: string,
  chipRefId: string,
  sourceText: string | null,
  chip?: HTMLElement | null,
): ReferenceRowLookupResult<T> {
  if (isRowScopedReferenceId(chipRefId)) {
    return resolveChipRefRowForOccurrence(refs, sourceBlockId, chipRefId);
  }
  const occurrenceIndex = chip ? countChipOccurrenceIndex(chip, chipRefId) : 0;
  const excludeRowIds = sourceText ? collectClaimedRowIds(sourceText) : undefined;
  return resolveChipRefRowForOccurrence(refs, sourceBlockId, chipRefId, {
    occurrenceIndex,
    excludeRowIds,
  });
}

export type EditLinkNodeMeta = {
  nodeId: string;
  bookId: string;
  language: Language;
};

export type EditLinkOpenResult = {
  mode: 'create' | 'edit';
  sourceRef: EditLinkSourceRef;
};

export type EditLinkResolveContext = {
  refs: EditLinkRefRow[] | undefined;
  getSourceText: (sourceBlockId: string) => string | null;
  meta: EditLinkNodeMeta;
};

/** DocumentData UUID for the block containing this chip. */
export function resolveSourceBlockIdFromChip(chip: HTMLElement): string {
  const ceBlock = chip.closest<HTMLElement>('.ce-block');
  if (ceBlock) {
    const docId = ceBlock.getAttribute('data-doc-id');
    if (docId) return docId;
    const editorId = ceBlock.getAttribute('data-id');
    if (editorId) return editorId;
  }
  const dataHost = chip.closest<HTMLElement>('[data-id]:not(.ref-chip)');
  const dataId = dataHost?.getAttribute('data-id');
  if (dataId) return dataId;
  const idHost = chip.closest<HTMLElement>('[id]:not(.ref-chip)');
  return idHost?.id ?? '';
}

/** Index of `{{ref:chipRefId}}` in serialized source text. */
export function findTokenPosition(
  text: string,
  chipRefId: string,
): number {
  const idx = text.indexOf(tokenNeedle(chipRefId));
  return idx >= 0 ? idx : 0;
}

export function countChipOccurrenceIndex(
  chip: HTMLElement,
  chipRefId: string,
): number {
  const blockRoot =
    chip.closest<HTMLElement>('[contenteditable="true"]') ?? chip.parentElement;
  if (!blockRoot) return 0;
  const chips = [
    ...blockRoot.querySelectorAll<HTMLElement>('.ref-chip[data-ref-id]'),
  ];
  let occurrence = 0;
  for (const candidate of chips) {
    if (candidate.getAttribute('data-ref-id') === chipRefId) {
      if (candidate === chip) return occurrence;
      occurrence += 1;
    }
  }
  return 0;
}

export function findTokenPositionForChip(
  text: string,
  chipRefId: string,
  chip?: HTMLElement | null,
): number {
  if (isRowScopedReferenceId(chipRefId)) {
    return findTokenPosition(text, chipRefId);
  }
  const occurrence = chip ? countChipOccurrenceIndex(chip, chipRefId) : 0;
  return findTokenPositionAtOccurrence(text, chipRefId, occurrence) ?? 0;
}

/**
 * When the DOM selection is confined to a single ref-chip, return that chip.
 * Handles caret-inside-chip and parent-element selectNode(chip) ranges.
 */
export function getChipFromRange(range: Range): HTMLElement | null {
  const chipFromNode = (node: Node | null): HTMLElement | null => {
    if (!node) return null;
    const el =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as HTMLElement)
        : node.parentElement;
    return el?.closest?.('.ref-chip[data-ref-id]') ?? null;
  };

  const startChip = chipFromNode(range.startContainer);
  const endChip = chipFromNode(range.endContainer);
  if (startChip && startChip === endChip) {
    return startChip;
  }

  if (
    range.startContainer === range.endContainer &&
    range.startContainer.nodeType === Node.ELEMENT_NODE
  ) {
    const parent = range.startContainer as HTMLElement;
    for (let i = range.startOffset; i < range.endOffset; i++) {
      const child = parent.childNodes[i];
      if (child?.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (el.classList.contains('ref-chip') && el.hasAttribute('data-ref-id')) {
          return el;
        }
      }
    }
  }

  return null;
}

function buildEditSourceRefFromRow(
  row: EditLinkRefRow,
  chipRefId: string,
  sourceBlockId: string,
  sourceText: string | null,
  meta: EditLinkNodeMeta,
  chip?: HTMLElement | null,
  needsOwnRow?: boolean,
): EditLinkSourceRef {
  return {
    id: row.id,
    sourceId: sourceBlockId,
    nodeId: meta.nodeId,
    bookId: meta.bookId,
    refId: chipRefId,
    targetBlockId: row.refId,
    position: sourceText
      ? findTokenPositionForChip(sourceText, chipRefId, chip)
      : 0,
    sourceSelectionLength: 0,
    targetBookId: row.targetBookId ?? null,
    targetNodeId: row.targetNodeId ?? null,
    customName: row.customName ?? null,
    detectedPhrase: row.detectedPhrase ?? null,
    language: meta.language,
    isLost: isLostInternalReference({
      kind: row.kind,
      status: row.status,
      resolved: row.resolved,
    }),
    needsOwnRow,
  };
}

function buildCreateSourceRef(
  source: { sourceBlockId: string; position: number; length: number },
  meta: EditLinkNodeMeta,
): EditLinkSourceRef {
  return {
    id: null,
    sourceId: source.sourceBlockId,
    nodeId: meta.nodeId,
    bookId: meta.bookId,
    refId: null,
    position: source.position,
    sourceSelectionLength: source.length,
    targetBookId: null,
    targetNodeId: null,
    language: meta.language,
  };
}

/** Resolve open params from chip popover context (source block id + chip ref id). */
export function resolveEditLinkOpenFromIds(
  sourceBlockId: string,
  chipRefId: string,
  ctx: EditLinkResolveContext,
  chip?: HTMLElement | null,
): EditLinkOpenResult {
  const sourceText = ctx.getSourceText(sourceBlockId);
  const { row, needsOwnRow } = resolveOccurrenceAwareRow(
    ctx.refs,
    sourceBlockId,
    chipRefId,
    sourceText,
    chip,
  );
  if (row) {
    return {
      mode: 'edit',
      sourceRef: buildEditSourceRefFromRow(
        row,
        chipRefId,
        sourceBlockId,
        sourceText,
        ctx.meta,
        chip,
        needsOwnRow,
      ),
    };
  }
  return {
    mode: 'create',
    sourceRef: {
      id: null,
      sourceId: sourceBlockId,
      nodeId: ctx.meta.nodeId,
      bookId: ctx.meta.bookId,
      refId: chipRefId,
      position: 0,
      sourceSelectionLength: 0,
      targetBookId: null,
      targetNodeId: null,
      language: ctx.meta.language,
    },
  };
}

/** Chip-only selection → edit; otherwise create via resolvePickerSource. */
export function resolveEditLinkOpenFromRange(
  range: Range,
  idMapping: Map<string, string>,
  ctx: EditLinkResolveContext,
): EditLinkOpenResult | null {
  const chip = getChipFromRange(range);
  if (chip) {
    const sourceBlockId = resolveSourceBlockIdFromChip(chip);
    const chipRefId = chip.getAttribute('data-ref-id') ?? '';
    const sourceText = ctx.getSourceText(sourceBlockId);
    const { row, needsOwnRow } = resolveOccurrenceAwareRow(
      ctx.refs,
      sourceBlockId,
      chipRefId,
      sourceText,
      chip,
    );
    if (row) {
      return {
        mode: 'edit',
        sourceRef: buildEditSourceRefFromRow(
          row,
          chipRefId,
          sourceBlockId,
          sourceText,
          ctx.meta,
          chip,
          needsOwnRow,
        ),
      };
    }
  }

  const source = resolvePickerSource(range, idMapping);
  if (source) {
    return {
      mode: 'create',
      sourceRef: buildCreateSourceRef(source, ctx.meta),
    };
  }

  const startEl =
    range.startContainer.nodeType === Node.ELEMENT_NODE
      ? (range.startContainer as HTMLElement)
      : range.startContainer.parentElement;
  const ceBlock = startEl?.closest<HTMLElement>('.ce-block');
  if (ceBlock) {
    const docId = ceBlock.getAttribute('data-doc-id');
    const editorId = ceBlock.getAttribute('data-id');
    const sourceBlockId =
      docId ??
      (editorId ? idMapping.get(editorId) ?? editorId : '');
    const walkRoot = resolveSerializedWalkRoot(ceBlock);
    const offsets = computeSerializedOffsets(walkRoot, range);
    if (offsets && sourceBlockId) {
      return {
        mode: 'create',
        sourceRef: buildCreateSourceRef(
          {
            sourceBlockId,
            position: offsets.position,
            length: offsets.length,
          },
          ctx.meta,
        ),
      };
    }
  }

  const host = startEl?.closest<HTMLElement>(
    '[data-id][contenteditable="true"]',
  );
  if (host) {
    const sourceBlockId =
      host.getAttribute('data-doc-id') ??
      host.getAttribute('data-id') ??
      '';
    const walkRoot = resolveSerializedWalkRoot(host);
    const offsets = computeSerializedOffsets(walkRoot, range);
    if (offsets && sourceBlockId) {
      return {
        mode: 'create',
        sourceRef: buildCreateSourceRef(
          {
            sourceBlockId,
            position: offsets.position,
            length: offsets.length,
          },
          ctx.meta,
        ),
      };
    }
  }

  return null;
}

export type EditLinkListResolveContext = {
  /** Stored list items (tokenized/canonical form), in order. */
  items: string[];
  /** DocumentData UUID of the list block. */
  sourceBlockId: string;
  refs: EditLinkRefRow[] | undefined;
  meta: EditLinkNodeMeta;
};

/**
 * List-specific variant of `resolveEditLinkOpenFromRange`.
 *
 * A list is a single block whose content lives in `items: string[]`, but the
 * generic resolver walks the whole `.ce-block__content` — which counts the
 * `<ul>`/`<li>` tag lengths (they're in ALLOWED_TAGS) and so produces an
 * offset in a different coordinate space than the stored, tag-free items.
 *
 * Instead, compute the offset relative to the specific item-content element
 * and add the summed lengths of the preceding items, yielding a position in
 * the tag-free `items.join('')` space that `resolveListItemAtOffset` expects.
 */
export function resolveEditLinkOpenFromListRange(
  range: Range,
  ctx: EditLinkListResolveContext,
): EditLinkOpenResult | null {
  const joinedText = ctx.items.join('');

  const chip = getChipFromRange(range);
  if (chip) {
    const chipRefId = chip.getAttribute('data-ref-id') ?? '';
    const { row, needsOwnRow } = resolveOccurrenceAwareRow(
      ctx.refs,
      ctx.sourceBlockId,
      chipRefId,
      joinedText,
      chip,
    );
    if (row) {
      return {
        mode: 'edit',
        sourceRef: buildEditSourceRefFromRow(
          row,
          chipRefId,
          ctx.sourceBlockId,
          joinedText,
          ctx.meta,
          chip,
          needsOwnRow,
        ),
      };
    }
  }

  const startEl =
    range.startContainer.nodeType === Node.ELEMENT_NODE
      ? (range.startContainer as HTMLElement)
      : range.startContainer.parentElement;
  const itemEl = startEl?.closest<HTMLElement>('.cdx-list__item') ?? null;
  if (!itemEl) return null;
  const walkRoot =
    itemEl.querySelector<HTMLElement>('.cdx-list__item-content') ?? itemEl;

  const listRoot = itemEl.closest<HTMLElement>('.cdx-list');
  const itemEls = listRoot
    ? [...listRoot.querySelectorAll<HTMLElement>('.cdx-list__item')]
    : [itemEl];
  const itemIndex = itemEls.indexOf(itemEl);
  if (itemIndex < 0) return null;

  const offsets = computeSerializedOffsets(walkRoot, range);
  if (!offsets) return null;

  const prefix = ctx.items
    .slice(0, itemIndex)
    .reduce((sum, item) => sum + item.length, 0);

  return {
    mode: 'create',
    sourceRef: buildCreateSourceRef(
      {
        sourceBlockId: ctx.sourceBlockId,
        position: prefix + offsets.position,
        length: offsets.length,
      },
      ctx.meta,
    ),
  };
}
